import type { Dimension, MatrixViewStateShape, ResolvedPalier } from '@mss_creator/shared';

/** The three export formats (2026-08-09 design doc, decision 3). */
export type ExportFormat = 'excel' | 'pdf' | 'html';

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  excel: 'Excel',
  pdf: 'PDF',
  html: 'HTML',
};

/**
 * The export page's own selection state (2026-08-09 design doc, decision 4:
 * columns + dimensions + activation-row only, no individual cells). Fully
 * local/ephemeral (decision 5) — seeded once from the matrix's `viewState` as
 * a default, never written back to it.
 */
export interface ExportSelectionState {
  includedColumns: number[];
  includedDimensionIds: string[];
  includeActivationRow: boolean;
  /**
   * How the six activation rows are named in the export — mirrors
   * `exportSelectionSchema.activationLevierNames`. A display choice, not a
   * selection, so `isEverythingSelected` deliberately ignores it.
   */
  activationLevierNames: 'hidden' | 'column' | 'inline';
  /**
   * Whether the export ends with the notes page (issue #19) - mirrors
   * `exportSelectionSchema.includeClientNotes`. Off by default, like the rest of
   * the selection it is not kept by the presets, and `isEverythingSelected`
   * ignores it.
   */
  includeClientNotes: boolean;
}

/**
 * Default selection = the matrix's currently visible state (plan-mss.md §9):
 * hidden columns/dimensions/a collapsed activation row start unchecked, but
 * stay toggleable ("réactivables pour l'export" — see decision 5, they're
 * never disabled).
 */
export function defaultExportSelection(
  viewState: MatrixViewStateShape,
  paliers: ResolvedPalier[],
  dimensions: Dimension[],
): ExportSelectionState {
  return {
    includedColumns: paliers
      .map((palier) => palier.number)
      .filter((number) => !viewState.hiddenColumns.includes(number)),
    includedDimensionIds: dimensions
      .map((dimension) => dimension.id)
      .filter((id) => !viewState.hiddenDimensionIds.includes(id)),
    includeActivationRow: !viewState.activationRowCollapsed,
    activationLevierNames: 'hidden',
    includeClientNotes: false,
  };
}

/**
 * Every column/dimension included, activation row included — the export
 * page's own "tout afficher" (decision 5: purely local, unlike the toolbar's
 * version it never calls `performVisibility` or touches the live `viewState`).
 */
export function allSelectedExportSelection(
  paliers: ResolvedPalier[],
  dimensions: Dimension[],
): ExportSelectionState {
  return {
    includedColumns: paliers.map((palier) => palier.number),
    includedDimensionIds: dimensions.map((dimension) => dimension.id),
    includeActivationRow: true,
    activationLevierNames: 'hidden',
    includeClientNotes: false,
  };
}

export function toggleColumn(
  selection: ExportSelectionState,
  columnNumber: number,
): ExportSelectionState {
  return {
    ...selection,
    includedColumns: selection.includedColumns.includes(columnNumber)
      ? selection.includedColumns.filter((number) => number !== columnNumber)
      : [...selection.includedColumns, columnNumber],
  };
}

export function toggleDimension(
  selection: ExportSelectionState,
  dimensionId: string,
): ExportSelectionState {
  return {
    ...selection,
    includedDimensionIds: selection.includedDimensionIds.includes(dimensionId)
      ? selection.includedDimensionIds.filter((id) => id !== dimensionId)
      : [...selection.includedDimensionIds, dimensionId],
  };
}

export function toggleActivationRow(selection: ExportSelectionState): ExportSelectionState {
  return { ...selection, includeActivationRow: !selection.includeActivationRow };
}

/** « Inclure les notes de niveau Client » (issue #19) - a display choice like the levier names, not a selection. */
export function setIncludeClientNotes(
  selection: ExportSelectionState,
  include: boolean,
): ExportSelectionState {
  return { ...selection, includeClientNotes: include };
}

/** Applies to every format — including Excel, which has no Tableau category,
 *  so there the control moves under Contenu. */
export function setActivationLevierNames(
  selection: ExportSelectionState,
  mode: ExportSelectionState['activationLevierNames'],
): ExportSelectionState {
  return { ...selection, activationLevierNames: mode };
}

/**
 * True once every column/dimension is included and the activation row isn't
 * excluded — drives the export page's own "tout afficher" active-state
 * styling, mirroring the toolbar's `hasHiddenItems` (matrix-toolbar.tsx).
 */
export function isEverythingSelected(
  selection: ExportSelectionState,
  paliers: ResolvedPalier[],
  dimensions: Dimension[],
): boolean {
  return (
    selection.includedColumns.length === paliers.length &&
    selection.includedDimensionIds.length === dimensions.length &&
    selection.includeActivationRow
  );
}

/** "exporter en {Format}" — the mockup's button label (Écran 33). A format is
 *  always selected (Excel is the entry default), so there is no unselected
 *  label any more. */
export function exportButtonLabel(format: ExportFormat): string {
  return `exporter en ${EXPORT_FORMAT_LABELS[format]}`;
}
